import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '@/components/DataTable';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { EditOnly } from '@/components/EditOnly';
import { MediaUpload } from '@/components/MediaUpload';
import { Select } from '@/components/Select';
import { TrackerLinkPicker } from '@/components/TrackerLinkPicker';
import { TargetingForm, formatTargeting, LANGUAGES } from '@/components/TargetingForm';
import { COUNTRIES } from '@/lib/countries';
import { formatDate, formatDateTime } from '@/lib/datetime';
import {
  getAds,
  createAd,
  updateAd,
  deleteAd,
  archiveAds,
  duplicateAds,
  uploadMedia,
  type AdsFilters,
} from '@/api/ads';
import { getCampaigns } from '@/api/campaigns';
import { getOffers } from '@/api/offers';
import { getPwaApps } from '@/api/pwa';
import type {
  AdWithTargeting,
  Campaign,
  CreateAdInput,
  UpdateAdInput,
} from '@/api/types';

const COUNTRY_OPTIONS = [
  { value: '', label: 'All countries' },
  ...COUNTRIES.map((c) => ({
    value: c.code,
    label: `${c.name} (${c.code})`,
    icon: <span className="text-base leading-none">{c.flag}</span>,
  })),
];

type ModalMode = 'create' | 'edit' | 'delete' | null;

interface TargetingData {
  countries: string | null;
  platforms: string | null;
  device_types: string | null;
  languages: string | null;
}

interface FormData {
  campaign_id: string;
  ad_type: 'interstitial' | 'rewarded' | 'banner' | 'native' | 'popup';
  media_type: 'image' | 'video' | 'html' | 'native';
  media_url: string;
  click_url: string;
  duration_sec: number | null;
  lock_webview: boolean;
  icon_url: string;
  ad_text: string;
  ad_body: string;
  cta_text: string;
  cta_text_2: string;
  popup_delay_sec: number | null;
  popup_repeat_sec: number | null;
  store_url: string;
  status: 'active' | 'paused';
  targeting: TargetingData;
}

const initialFormData: FormData = {
  campaign_id: '',
  ad_type: 'interstitial',
  media_type: 'image',
  media_url: '',
  click_url: '',
  duration_sec: null,
  lock_webview: false,
  icon_url: '',
  ad_text: '',
  ad_body: '',
  cta_text: '',
  cta_text_2: '',
  popup_delay_sec: null,
  popup_repeat_sec: null,
  store_url: '',
  status: 'active',
  targeting: {
    countries: null,
    platforms: null,
    device_types: null,
    languages: null,
  },
};

const AD_TYPE_LABELS: Record<string, string> = {
  interstitial: 'Interstitial',
  rewarded: 'Rewarded',
  banner: 'Banner',
  native: 'Custom Banner',
  popup: 'Popup',
};

const AD_TYPE_COLORS: Record<string, string> = {
  interstitial: 'bg-primary/10 text-primary',
  rewarded: 'bg-warning-soft text-warning',
  banner: 'bg-info-soft text-info',
  native: 'bg-success-soft text-success',
  popup: 'bg-warning-soft text-warning',
};

const MEDIA_TYPE_LABELS: Record<string, string> = {
  image: 'Image',
  video: 'Video',
  html: 'HTML',
  native: 'Custom Banner',
};

// Recommended media sizes for each ad type
const MEDIA_SPECS: Record<string, Record<string, string>> = {
  interstitial: {
    image: '1080×1920 (portrait) or 1920×1080 (landscape)',
    video: '1080×1920, 15-30 sec, MP4 H.264, web-optimized (faststart)',
    html: 'Fullscreen HTML page (.html file)',
    native: 'Icon: 128×128 square + Headline + Body + CTA',
  },
  rewarded: {
    image: '1080×1920 (portrait) or 1920×1080 (landscape)',
    video: '1080×1920, 15-30 sec, MP4 H.264, web-optimized (faststart)',
    html: 'Fullscreen HTML page (.html file)',
    native: 'Icon: 128×128 square + Headline + Body + CTA',
  },
  banner: {
    image: '320×50 (phone) or 728×90 (tablet)',
    video: 'Not recommended for banners',
    html: 'HTML banner page (.html file)',
    native: 'Icon: 64×64 square + Headline text',
  },
};

// Color-coded conversion-rate cell. Subtle tint so columns stay scannable.
function CrCell({ value }: { value: number | undefined }) {
  const v = value ?? 0;
  // Empty / no data — dim
  if (!v) {
    return <span className="text-content-subtle text-sm tabular-nums">—</span>;
  }
  // Thresholds are generic; tune per vertical if needed.
  const color =
    v >= 20 ? 'text-success'
    : v >= 10 ? 'text-success'
    : v >= 5 ? 'text-warning'
    : 'text-content-subtle';
  return (
    <span className={`${color} text-sm font-medium tabular-nums`}>
      {v.toFixed(v >= 10 ? 1 : 2)}%
    </span>
  );
}

export function Ads() {
  const queryClient = useQueryClient();
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedAd, setSelectedAd] = useState<AdWithTargeting | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [formError, setFormError] = useState('');
  const [campaignFilter, setCampaignFilter] = useState<string>('');
  const [adTypeFilter, setAdTypeFilter] = useState<string>('');
  const [mediaTypeFilter, setMediaTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [countryFilter, setCountryFilter] = useState<string>('');
  const [platformFilter, setPlatformFilter] = useState<string>('');
  const [languageFilter, setLanguageFilter] = useState<string>('');
  const [hideTest, setHideTest] = useState(true);
  const [statsStartDate, setStatsStartDate] = useState<string>('');
  const [statsEndDate, setStatsEndDate] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAdIds, setSelectedAdIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateTargetCampaign, setDuplicateTargetCampaign] = useState('');

  // Query for campaigns. Include archived so archived ads still resolve their
  // campaign name; the selectable dropdowns below filter archived out.
  const { data: campaigns = [] } = useQuery({
    queryKey: ['campaigns', 'all'],
    queryFn: () => getCampaigns({ includeArchived: true }),
  });

  // Only non-archived campaigns are selectable for new/edited ads and the filter.
  const activeCampaigns = useMemo(
    () => campaigns.filter((c) => !c.archived_at),
    [campaigns]
  );

  // Query for fetching offers (for offer GEOs button)
  const { data: offers = [] } = useQuery({
    queryKey: ['offers'],
    queryFn: getOffers,
  });

  // Query for fetching PWA apps (for "Link source: PWA" picker in ad form)
  const { data: pwaApps = [] } = useQuery({
    queryKey: ['pwaApps'],
    queryFn: getPwaApps,
  });

  // Base URL used to build PWA landing click_urls.
  // Prefer the dedicated PWA custom domain; fall back to the API origin.
  const pwaBaseUrl = useMemo(
    () =>
      import.meta.env.VITE_PWA_BASE_URL ||
      'https://newads-api.newoneads.workers.dev',
    []
  );
  const buildPwaClickUrl = (pwaId: string) => `${pwaBaseUrl}/pwa/${pwaId}`;
  const extractPwaIdFromUrl = (url: string): string | null => {
    const m = url.match(/\/pwa\/([0-9a-f-]{36})/i);
    return m ? m[1] : null;
  };

  const [linkSource, setLinkSource] = useState<'pwa' | 'custom'>('custom');

  // Collect all GEOs covered by offers
  const offerGeos = useMemo(() => {
    const geos = new Set<string>();
    offers.forEach((offer) => {
      offer.geo.split(',').map((s) => s.trim()).filter(Boolean).forEach((code) => geos.add(code));
    });
    return Array.from(geos);
  }, [offers]);

  // Create campaign lookup map
  const campaignMap = campaigns.reduce<Record<string, Campaign>>((acc, c) => {
    acc[c.id] = c;
    return acc;
  }, {});

  // Build filters object
  const filters: AdsFilters = useMemo(() => ({
    campaignId: campaignFilter || undefined,
    adType: adTypeFilter || undefined,
    mediaType: mediaTypeFilter || undefined,
    status: statusFilter || undefined,
    search: searchFilter || undefined,
    country: countryFilter || undefined,
    platform: platformFilter || undefined,
    language: languageFilter || undefined,
    page,
    pageSize,
    sortBy,
    sortOrder,
    startDate: statsStartDate || undefined,
    endDate: statsEndDate || undefined,
    hideTest,
  }), [campaignFilter, adTypeFilter, mediaTypeFilter, statusFilter, searchFilter, countryFilter, platformFilter, languageFilter, page, pageSize, sortBy, sortOrder, statsStartDate, statsEndDate, hideTest]);

  // Query for fetching ads with pagination
  const {
    data: adsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['ads', filters],
    queryFn: () => getAds(filters),
  });

  const ads = adsData?.ads || [];
  const totalPages = adsData?.total_pages || 1;
  const totalAds = adsData?.total || 0;

  // The "Hide test" filter is applied server-side (see AdsFilters.hideTest), so
  // pagination and the total count already exclude test ads — no client-side
  // trimming, which would only shrink the current page and desync the counter.
  const filteredAds = ads;

  // Mutation for creating ad
  const createMutation = useMutation({
    mutationFn: (data: CreateAdInput) => createAd(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ads'] });
      closeModal();
    },
    onError: (err: Error) => {
      setFormError(err.message);
    },
  });

  // Mutation for updating ad
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAdInput }) =>
      updateAd(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ads'] });
      closeModal();
    },
    onError: (err: Error) => {
      setFormError(err.message);
    },
  });

  // Mutation for deleting ad
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteAd(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ads'] });
      closeModal();
    },
    onError: (err: Error) => {
      setFormError(err.message);
    },
  });

  // Mutation for duplicating ads
  const duplicateMutation = useMutation({
    mutationFn: ({ adIds, targetCampaignId }: { adIds: string[]; targetCampaignId: string }) =>
      duplicateAds(adIds, targetCampaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ads'] });
      setShowDuplicateModal(false);
      setSelectedAdIds(new Set());
      setDuplicateTargetCampaign('');
    },
    onError: (err: Error) => {
      setFormError(err.message);
    },
  });

  // Mutation for archiving ads (one-way)
  const archiveMutation = useMutation({
    mutationFn: (adIds: string[]) => archiveAds(adIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ads'] });
      setSelectedAdIds(new Set());
    },
    onError: (err: Error) => {
      setFormError(err.message);
    },
  });

  const handleArchiveSelected = () => {
    const count = selectedAdIds.size;
    if (count === 0) return;
    if (!window.confirm(
      `Archive ${count} ad${count > 1 ? 's' : ''}? This is irreversible — archived ads are hidden from the list and stop serving, but stay available for statistics.`
    )) return;
    archiveMutation.mutate(Array.from(selectedAdIds));
  };

  const activeFilterCount = [adTypeFilter, mediaTypeFilter, statusFilter, searchFilter, countryFilter, platformFilter, languageFilter, statsStartDate, statsEndDate].filter(Boolean).length;

  const resetFilters = () => {
    setAdTypeFilter('');
    setMediaTypeFilter('');
    setStatusFilter('');
    setSearchFilter('');
    setSearchInput('');
    setCountryFilter('');
    setPlatformFilter('');
    setLanguageFilter('');
    setStatsStartDate('');
    setStatsEndDate('');
    setPage(1);
  };

  const handleSearchSubmit = () => {
    setSearchFilter(searchInput.trim());
    setPage(1);
  };

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(key);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const toggleSelectAd = (adId: string) => {
    setSelectedAdIds((prev) => {
      const next = new Set(prev);
      if (next.has(adId)) {
        next.delete(adId);
      } else {
        next.add(adId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedAdIds.size === filteredAds.length) {
      setSelectedAdIds(new Set());
    } else {
      setSelectedAdIds(new Set(filteredAds.map((ad) => ad.id)));
    }
  };

  const handleDuplicate = () => {
    if (!duplicateTargetCampaign) return;
    duplicateMutation.mutate({
      adIds: Array.from(selectedAdIds),
      targetCampaignId: duplicateTargetCampaign,
    });
  };

  const openCreateModal = () => {
    setFormData({
      ...initialFormData,
      campaign_id: campaignFilter || (activeCampaigns[0]?.id || ''),
    });
    setLinkSource('custom');
    setFormError('');
    setSelectedAd(null);
    setModalMode('create');
  };

  const openEditModal = (ad: AdWithTargeting) => {
    setFormData({
      campaign_id: ad.campaign_id,
      ad_type: ad.ad_type,
      media_type: ad.media_type,
      media_url: ad.media_url,
      click_url: ad.click_url || '',
      duration_sec: ad.duration_sec,
      lock_webview: ad.lock_webview ?? false,
      icon_url: ad.icon_url || '',
      ad_text: ad.ad_text || '',
      ad_body: ad.ad_body || '',
      cta_text: ad.cta_text || '',
      cta_text_2: ad.cta_text_2 || '',
      popup_delay_sec: ad.popup_delay_sec ?? null,
      popup_repeat_sec: ad.popup_repeat_sec ?? null,
      store_url: ad.store_url || '',
      status: ad.status,
      targeting: {
        countries: ad.targeting?.countries || null,
        platforms: ad.targeting?.platforms || null,
        device_types: ad.targeting?.device_types || null,
        languages: ad.targeting?.languages || null,
      },
    });
    setLinkSource(extractPwaIdFromUrl(ad.click_url || '') ? 'pwa' : 'custom');
    setFormError('');
    setSelectedAd(ad);
    setModalMode('edit');
  };

  const openDeleteModal = (ad: AdWithTargeting) => {
    setSelectedAd(ad);
    setFormError('');
    setModalMode('delete');
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedAd(null);
    setFormData(initialFormData);
    setFormError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!formData.campaign_id) {
      setFormError('Campaign is required');
      return;
    }

    if (formData.ad_type === 'popup') {
      if (!formData.ad_text.trim()) {
        setFormError('Title is required for popup ads');
        return;
      }
      if (!formData.click_url.trim()) {
        setFormError('Link URL is required for popup ads');
        return;
      }
      if (!formData.cta_text.trim()) {
        setFormError('Button 1 text is required for popup ads');
        return;
      }
    } else if (formData.media_type === 'native') {
      if (!formData.icon_url) {
        setFormError('Icon is required for native ads');
        return;
      }
      if (!formData.ad_text.trim()) {
        setFormError('Headline is required for native ads');
        return;
      }
      if (!formData.click_url.trim()) {
        setFormError('Click URL is required for native ads');
        return;
      }
    } else {
      if (!formData.media_url) {
        setFormError('Media file is required');
        return;
      }
      if (formData.media_type !== 'html' && !formData.click_url.trim()) {
        setFormError('Click URL is required');
        return;
      }
    }

    const isPopup = formData.ad_type === 'popup';
    const isNative = formData.media_type === 'native';
    const payload = {
      campaign_id: formData.campaign_id,
      ad_type: formData.ad_type,
      media_type: isPopup ? 'image' as const : formData.media_type,
      media_url: isPopup ? (formData.media_url || '') : (isNative ? (formData.media_url || '') : formData.media_url),
      click_url: formData.media_type === 'html' && !isPopup ? null : formData.click_url.trim(),
      duration_sec: formData.media_type === 'video' ? formData.duration_sec : null,
      lock_webview: formData.lock_webview,
      icon_url: isNative ? formData.icon_url : null,
      ad_text: (isNative || isPopup) ? formData.ad_text.trim() : null,
      ad_body: (isNative || isPopup) ? formData.ad_body.trim() || null : null,
      cta_text: (isNative || isPopup) ? formData.cta_text.trim() || null : null,
      cta_text_2: isPopup ? formData.cta_text_2.trim() || null : null,
      popup_delay_sec: isPopup ? formData.popup_delay_sec : null,
      popup_repeat_sec: isPopup ? formData.popup_repeat_sec : null,
      store_url: formData.store_url.trim() || null,
      status: formData.status,
      targeting: {
        countries: formData.targeting.countries,
        platforms: formData.targeting.platforms,
        device_types: formData.targeting.device_types,
        languages: formData.targeting.languages,
      },
    };

    if (modalMode === 'create') {
      createMutation.mutate(payload);
    } else if (modalMode === 'edit' && selectedAd) {
      updateMutation.mutate({
        id: selectedAd.id,
        data: payload,
      });
    }
  };

  const handleDelete = () => {
    if (selectedAd) {
      deleteMutation.mutate(selectedAd.id);
    }
  };

  const handleUpload = async (file: File): Promise<string> => {
    return uploadMedia(file);
  };

  const columns = [
    {
      key: 'select',
      label: '',
      render: (ad: AdWithTargeting) => (
        <input
          type="checkbox"
          checked={selectedAdIds.has(ad.id)}
          onChange={(e) => {
            e.stopPropagation();
            toggleSelectAd(ad.id);
          }}
          onClick={(e) => e.stopPropagation()}
          className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary cursor-pointer"
        />
      ),
    },
    {
      key: 'campaign',
      label: 'Campaign',
      render: (ad: AdWithTargeting) => {
        const campaign = campaignMap[ad.campaign_id];
        return (
          <span className="font-medium text-content">
            {campaign?.name || 'Unknown Campaign'}
          </span>
        );
      },
    },
    {
      key: 'ad_type',
      label: 'Type',
      sortKey: 'ad_type',
      render: (ad: AdWithTargeting) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${AD_TYPE_COLORS[ad.ad_type]}`}
        >
          {AD_TYPE_LABELS[ad.ad_type]}
        </span>
      ),
    },
    {
      key: 'media_type',
      label: 'Media Type',
      sortKey: 'media_type',
      render: (ad: AdWithTargeting) => (
        <span className="text-content-muted text-sm flex items-center gap-1">
          {ad.media_type === 'image' ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          ) : ad.media_type === 'html' ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
          ) : ad.media_type === 'native' ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          )}
          {MEDIA_TYPE_LABELS[ad.media_type]}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortKey: 'status',
      render: (ad: AdWithTargeting) => {
        if (ad.archived_at) {
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface-muted text-content-subtle">
              Archived
            </span>
          );
        }
        const statusStyles = {
          active: 'bg-success-soft text-success',
          paused: 'bg-surface-muted text-content-muted',
        };
        const statusLabels = {
          active: 'Active',
          paused: 'Paused',
        };
        return (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusStyles[ad.status]}`}
          >
            {statusLabels[ad.status]}
          </span>
        );
      },
    },
    {
      key: 'impressions',
      label: 'Impr',
      sortKey: 'impressions',
      render: (ad: AdWithTargeting) => (
        <span className="text-content-muted text-sm font-medium tabular-nums">
          {(ad.stats?.impressions || 0).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'clicks',
      label: 'Clicks',
      sortKey: 'clicks',
      render: (ad: AdWithTargeting) => (
        <span className="text-content-muted text-sm font-medium tabular-nums">
          {(ad.stats?.clicks || 0).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'ctr',
      label: 'CTR',
      sortKey: 'ctr',
      render: (ad: AdWithTargeting) => (
        <span className="text-content-muted text-sm font-medium tabular-nums">
          {(ad.stats?.ctr || 0).toFixed(2)}%
        </span>
      ),
    },
    {
      key: 'installs',
      label: 'Installs',
      sortKey: 'installs',
      render: (ad: AdWithTargeting) => (
        <span className="text-content-muted text-sm font-medium tabular-nums">
          {(ad.stats?.installs || 0).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'registrations',
      label: 'Reg',
      sortKey: 'registrations',
      render: (ad: AdWithTargeting) => (
        <span className="text-content-muted text-sm font-medium tabular-nums">
          {(ad.stats?.registrations || 0).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'deposits',
      label: 'Dep',
      sortKey: 'deposits',
      render: (ad: AdWithTargeting) => (
        <span className="text-content-muted text-sm font-medium tabular-nums">
          {(ad.stats?.deposits || 0).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'i2r',
      label: 'I→R',
      sortKey: 'i2r',
      render: (ad: AdWithTargeting) => <CrCell value={ad.stats?.i2r} />,
    },
    {
      key: 'r2d',
      label: 'R→D',
      sortKey: 'r2d',
      render: (ad: AdWithTargeting) => <CrCell value={ad.stats?.r2d} />,
    },
    {
      key: 'i2d',
      label: 'I→D',
      sortKey: 'i2d',
      render: (ad: AdWithTargeting) => <CrCell value={ad.stats?.i2d} />,
    },
    {
      key: 'targeting',
      label: 'Targeting',
      render: (ad: AdWithTargeting) => (
        <span className="text-content-muted text-sm">
          {formatTargeting(ad.targeting)}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      sortKey: 'created_at',
      render: (ad: AdWithTargeting) => (
        <span className="text-content-subtle text-xs whitespace-nowrap">
          {formatDate(ad.created_at)}
        </span>
      ),
    },
    {
      key: 'updated_at',
      label: 'Updated',
      sortKey: 'updated_at',
      render: (ad: AdWithTargeting) => (
        <span className="text-content-subtle text-xs whitespace-nowrap">
          {ad.updated_at ? formatDate(ad.updated_at) : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (ad: AdWithTargeting) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              openEditModal(ad);
            }}
            className="text-content-subtle hover:text-content-muted transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              openDeleteModal(ad);
            }}
            className="text-content-subtle hover:text-danger transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="bg-danger-soft border border-danger/30 rounded-lg p-4">
        <p className="text-sm text-danger">
          Failed to load ads:{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  const renderLinkUrlField = (labelText: string, idSuffix: string) => {
    const selectedPwaId = extractPwaIdFromUrl(formData.click_url);
    return (
      <div>
        <label className="block text-xs font-medium text-content-subtle mb-1">{labelText}</label>
        <div className="inline-flex rounded-md border border-border-strong overflow-hidden mb-2 text-xs">
          <button
            type="button"
            onClick={() => {
              setLinkSource('pwa');
              if (!extractPwaIdFromUrl(formData.click_url) && pwaApps.length > 0) {
                const firstActive = pwaApps.find((p) => p.status === 'active') || pwaApps[0];
                setFormData((prev) => ({ ...prev, click_url: buildPwaClickUrl(firstActive.id) }));
              }
            }}
            className={`px-3 py-1 ${linkSource === 'pwa' ? 'bg-primary text-white' : 'bg-surface text-content-muted hover:bg-surface-muted'}`}
          >
            PWA
          </button>
          <button
            type="button"
            onClick={() => {
              setLinkSource('custom');
              if (extractPwaIdFromUrl(formData.click_url)) {
                setFormData((prev) => ({ ...prev, click_url: '' }));
              }
            }}
            className={`px-3 py-1 border-l border-border-strong ${linkSource === 'custom' ? 'bg-primary text-white' : 'bg-surface text-content-muted hover:bg-surface-muted'}`}
          >
            Custom URL
          </button>
        </div>
        {linkSource === 'pwa' ? (
          <Select
            id={`pwa_select_${idSuffix}`}
            value={selectedPwaId || ''}
            placeholder="Select PWA"
            searchable
            options={pwaApps
              .filter((p) => p.status !== 'deleted')
              .map((p) => ({
                value: p.id,
                label: p.status === 'paused' ? `${p.app_name} (paused)` : p.app_name,
              }))}
            onChange={(v) =>
              setFormData((prev) => ({ ...prev, click_url: v ? buildPwaClickUrl(v) : '' }))
            }
          />
        ) : (
          <div className="flex items-start gap-2">
            <input
              type="url"
              id={`click_url_${idSuffix}`}
              value={formData.click_url}
              onChange={(e) => setFormData((prev) => ({ ...prev, click_url: e.target.value }))}
              className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="https://example.com/landing-page"
            />
            <div className="w-56 shrink-0">
              <TrackerLinkPicker onPick={(url) => setFormData((prev) => ({ ...prev, click_url: url }))} />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-content">Ads</h1>
          <p className="text-sm text-content-subtle mt-1">
            Manage your advertising creatives
          </p>
        </div>
        <EditOnly>
          <Button onClick={openCreateModal} disabled={activeCampaigns.length === 0}>
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create Ad
          </Button>
        </EditOnly>
      </div>

      {/* Filters */}
      <div className="mb-4 space-y-3">
        {/* Top row: Search + Campaign + Toggle filters */}
        <div className="flex items-end gap-3">
          {/* Search */}
          <div className="flex-1 max-w-xs">
            <label htmlFor="search-filter" className="block text-sm font-medium text-content-muted mb-1">Search</label>
            <div className="flex">
              <input
                id="search-filter"
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearchSubmit(); }}
                placeholder="URL, text, store..."
                className="block w-full px-3 py-2 border border-border-strong rounded-l-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              />
              <button
                type="button"
                onClick={handleSearchSubmit}
                className="px-3 py-2 bg-surface-muted border border-l-0 border-border-strong rounded-r-md hover:bg-surface-muted text-content-muted"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Campaign */}
          <div className="w-48">
            <label className="block text-sm font-medium text-content-muted mb-1">Campaign</label>
            <Select
              value={campaignFilter}
              onChange={(v) => { setCampaignFilter(v); setPage(1); }}
              placeholder="All"
              searchable
              options={[
                { value: '', label: 'All' },
                ...activeCampaigns.map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
          </div>

          {/* Status */}
          <div className="w-32">
            <label className="block text-sm font-medium text-content-muted mb-1">Status</label>
            <Select
              value={statusFilter}
              onChange={(v) => { setStatusFilter(v); setPage(1); }}
              placeholder="All"
              options={[
                { value: '', label: 'All' },
                { value: 'active', label: 'Active' },
                { value: 'paused', label: 'Paused' },
                { value: 'archived', label: 'Archived' },
              ]}
            />
          </div>

          {/* Ad Type */}
          <div className="w-36">
            <label className="block text-sm font-medium text-content-muted mb-1">Ad Type</label>
            <Select
              value={adTypeFilter}
              onChange={(v) => { setAdTypeFilter(v); setPage(1); }}
              placeholder="All"
              options={[
                { value: '', label: 'All' },
                { value: 'interstitial', label: 'Interstitial' },
                { value: 'rewarded', label: 'Rewarded' },
                { value: 'banner', label: 'Banner' },
                { value: 'native', label: 'Custom Banner' },
                { value: 'popup', label: 'Popup' },
              ]}
            />
          </div>

          {/* Media Type */}
          <div className="w-32">
            <label className="block text-sm font-medium text-content-muted mb-1">Media</label>
            <Select
              value={mediaTypeFilter}
              onChange={(v) => { setMediaTypeFilter(v); setPage(1); }}
              placeholder="All"
              options={[
                { value: '', label: 'All' },
                { value: 'image', label: 'Image' },
                { value: 'video', label: 'Video' },
                { value: 'html', label: 'HTML' },
                { value: 'native', label: 'Custom Banner' },
              ]}
            />
          </div>

          {/* More filters toggle */}
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 text-sm border rounded-md transition-colors ${showFilters || activeFilterCount > 0 ? 'bg-primary/10 border-primary/30 text-primary' : 'border-border-strong text-content-muted hover:bg-surface-muted'}`}
          >
            <span className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              More
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium bg-primary text-white rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </span>
          </button>
        </div>

        {/* Expanded filters row */}
        {showFilters && (
          <div className="flex items-end gap-3 p-3 bg-surface-muted rounded-lg border border-border">
            {/* Country */}
            <div className="w-48">
              <label htmlFor="country-filter" className="block text-sm font-medium text-content-muted mb-1">Country</label>
              <Select
                id="country-filter"
                value={countryFilter}
                onChange={(v) => { setCountryFilter(v); setPage(1); }}
                placeholder="All countries"
                options={COUNTRY_OPTIONS}
                searchable
              />
            </div>

            {/* Platform */}
            <div className="w-32">
              <label className="block text-sm font-medium text-content-muted mb-1">Platform</label>
              <Select
                value={platformFilter}
                onChange={(v) => { setPlatformFilter(v); setPage(1); }}
                placeholder="All"
                options={[
                  { value: '', label: 'All' },
                  { value: 'ios', label: 'iOS' },
                  { value: 'android', label: 'Android' },
                ]}
              />
            </div>

            {/* Language */}
            <div className="w-44">
              <label className="block text-sm font-medium text-content-muted mb-1">Language</label>
              <Select
                value={languageFilter}
                onChange={(v) => { setLanguageFilter(v); setPage(1); }}
                placeholder="All languages"
                searchable
                options={[
                  { value: '', label: 'All languages' },
                  ...LANGUAGES.map((l) => ({ value: l.code, label: `${l.name} (${l.code})` })),
                ]}
              />
            </div>

            {/* Stats Date From */}
            <div className="w-40">
              <label htmlFor="stats-start-date" className="block text-sm font-medium text-content-muted mb-1">Stats From</label>
              <input
                id="stats-start-date"
                type="date"
                value={statsStartDate}
                onChange={(e) => { setStatsStartDate(e.target.value); setPage(1); }}
                className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>

            {/* Stats Date To */}
            <div className="w-40">
              <label htmlFor="stats-end-date" className="block text-sm font-medium text-content-muted mb-1">Stats To</label>
              <input
                id="stats-end-date"
                type="date"
                value={statsEndDate}
                onChange={(e) => { setStatsEndDate(e.target.value); setPage(1); }}
                className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>

            {/* Hide test */}
            <label className="flex items-center gap-2 cursor-pointer select-none py-2">
              <input
                type="checkbox"
                checked={hideTest}
                onChange={(e) => { setHideTest(e.target.checked); setPage(1); }}
                className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
              />
              <span className="text-sm text-content-muted">Hide test</span>
            </label>

            {/* Reset */}
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={resetFilters}
                className="px-3 py-2 text-sm text-content-subtle hover:text-content-muted underline"
              >
                Reset filters
              </button>
            )}
          </div>
        )}

        {/* Active filters chips */}
        {activeFilterCount > 0 && !showFilters && (
          <div className="flex items-center gap-2 flex-wrap">
            {searchFilter && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-surface-muted text-content-muted text-xs rounded-full">
                Search: {searchFilter}
                <button type="button" onClick={() => { setSearchFilter(''); setSearchInput(''); setPage(1); }} className="text-content-subtle hover:text-content-muted">&times;</button>
              </span>
            )}
            {adTypeFilter && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-surface-muted text-content-muted text-xs rounded-full">
                {AD_TYPE_LABELS[adTypeFilter]}
                <button type="button" onClick={() => { setAdTypeFilter(''); setPage(1); }} className="text-content-subtle hover:text-content-muted">&times;</button>
              </span>
            )}
            {mediaTypeFilter && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-surface-muted text-content-muted text-xs rounded-full">
                {MEDIA_TYPE_LABELS[mediaTypeFilter]}
                <button type="button" onClick={() => { setMediaTypeFilter(''); setPage(1); }} className="text-content-subtle hover:text-content-muted">&times;</button>
              </span>
            )}
            {statusFilter && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-surface-muted text-content-muted text-xs rounded-full">
                {statusFilter === 'active' ? 'Active' : 'Paused'}
                <button type="button" onClick={() => { setStatusFilter(''); setPage(1); }} className="text-content-subtle hover:text-content-muted">&times;</button>
              </span>
            )}
            {countryFilter && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-surface-muted text-content-muted text-xs rounded-full">
                Country: {countryFilter}
                <button type="button" onClick={() => { setCountryFilter(''); setPage(1); }} className="text-content-subtle hover:text-content-muted">&times;</button>
              </span>
            )}
            {platformFilter && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-surface-muted text-content-muted text-xs rounded-full">
                {platformFilter === 'ios' ? 'iOS' : 'Android'}
                <button type="button" onClick={() => { setPlatformFilter(''); setPage(1); }} className="text-content-subtle hover:text-content-muted">&times;</button>
              </span>
            )}
            {languageFilter && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-surface-muted text-content-muted text-xs rounded-full">
                Language: {LANGUAGES.find((l) => l.code === languageFilter)?.name || languageFilter}
                <button type="button" onClick={() => { setLanguageFilter(''); setPage(1); }} className="text-content-subtle hover:text-content-muted">&times;</button>
              </span>
            )}
            {(statsStartDate || statsEndDate) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-info-soft text-info text-xs rounded-full">
                Stats: {statsStartDate || '...'} — {statsEndDate || '...'}
                <button type="button" onClick={() => { setStatsStartDate(''); setStatsEndDate(''); setPage(1); }} className="text-info hover:text-info">&times;</button>
              </span>
            )}
            <button
              type="button"
              onClick={resetFilters}
              className="text-xs text-content-subtle hover:text-content-muted underline"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* No campaigns warning */}
      {campaigns.length === 0 && !isLoading && (
        <div className="bg-warning-soft border border-warning/30 rounded-lg p-4 mb-4">
          <p className="text-sm text-warning">
            You need to create a campaign first before you can create ads.
          </p>
        </div>
      )}

      {/* Selection action bar */}
      {selectedAdIds.size > 0 && (
        <div className="mb-4 flex items-center gap-4 bg-primary/5 border border-primary/20 rounded-lg px-4 py-3">
          <input
            type="checkbox"
            checked={selectedAdIds.size === filteredAds.length}
            onChange={toggleSelectAll}
            className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary cursor-pointer"
          />
          <span className="text-sm font-medium text-content-muted">
            Selected: {selectedAdIds.size}
          </span>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setDuplicateTargetCampaign('');
              setFormError('');
              setShowDuplicateModal(true);
            }}
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Duplicate to Campaign
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={handleArchiveSelected}
            disabled={archiveMutation.isPending}
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8M10 12h4" />
            </svg>
            {archiveMutation.isPending ? 'Archiving…' : 'Archive'}
          </Button>
          <button
            type="button"
            onClick={() => setSelectedAdIds(new Set())}
            className="text-sm text-content-subtle hover:text-content-muted ml-auto"
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <DataTable
        columns={columns}
        data={filteredAds}
        loading={isLoading}
        onRowClick={openEditModal}
        keyExtractor={(ad) => ad.id}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-content-subtle">
            Page {page} of {totalPages} ({totalAds} ads)
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page <= 1}
              className="px-3 py-1.5 text-sm border border-border-strong rounded-md disabled:opacity-40 hover:bg-surface-muted"
            >
              First
            </button>
            <button
              type="button"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 text-sm border border-border-strong rounded-md disabled:opacity-40 hover:bg-surface-muted"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 text-sm border border-border-strong rounded-md disabled:opacity-40 hover:bg-surface-muted"
            >
              Next
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages}
              className="px-3 py-1.5 text-sm border border-border-strong rounded-md disabled:opacity-40 hover:bg-surface-muted"
            >
              Last
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal - Fullscreen compact layout */}
      <Modal
        isOpen={modalMode === 'create' || modalMode === 'edit'}
        onClose={closeModal}
        title={modalMode === 'create' ? 'Create Ad' : 'Edit Ad'}
        size="full"
      >
        <form onSubmit={handleSubmit} className="h-full flex flex-col">
          {formError && (
            <div className="p-2 bg-danger-soft border border-danger/30 rounded-md mb-3 flex-shrink-0">
              <p className="text-sm text-danger">{formError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 min-h-0 overflow-y-auto">
            {/* Left: Settings + Creative */}
            <div className="space-y-3">
              {/* Row: Campaign + Status */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-content-subtle mb-1">Campaign *</label>
                  <Select
                    value={formData.campaign_id}
                    onChange={(v) => setFormData((prev) => ({ ...prev, campaign_id: v }))}
                    placeholder="Select campaign"
                    searchable
                    size="sm"
                    options={[
                      { value: '', label: 'Select campaign' },
                      ...activeCampaigns.map((c) => ({ value: c.id, label: c.name })),
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-content-subtle mb-1">Status</label>
                  <Select
                    value={formData.status}
                    onChange={(v) => setFormData((prev) => ({ ...prev, status: v as 'active' | 'paused' }))}
                    size="sm"
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'paused', label: 'Paused' },
                    ]}
                  />
                </div>
              </div>

              {/* Row: Ad Type + Media Type + Lock WebView */}
              <div className="grid grid-cols-3 gap-3 items-end">
                <div>
                  <label className="block text-xs font-medium text-content-subtle mb-1">Ad Type</label>
                  <Select
                    value={formData.ad_type}
                    onChange={(v) => {
                      const val = v as FormData['ad_type'];
                      setFormData((prev) => ({
                        ...prev,
                        ad_type: val,
                        media_type: val === 'native' ? 'native' : (val === 'popup' ? 'image' : (val === 'banner' ? 'image' : prev.media_type)),
                      }));
                    }}
                    size="sm"
                    options={[
                      { value: 'interstitial', label: 'Interstitial' },
                      { value: 'rewarded', label: 'Rewarded' },
                      { value: 'banner', label: 'Banner' },
                      { value: 'native', label: 'Custom Banner' },
                      { value: 'popup', label: 'Popup' },
                    ]}
                  />
                </div>
                {formData.ad_type !== 'native' && formData.ad_type !== 'popup' && (
                <div>
                  <label className="block text-xs font-medium text-content-subtle mb-1">Media</label>
                  <Select
                    value={formData.media_type}
                    onChange={(v) =>
                      setFormData((prev) => ({
                        ...prev,
                        media_type: v as 'image' | 'video' | 'html' | 'native',
                        media_url: '',
                        icon_url: '',
                        ad_text: '',
                        ad_body: '',
                        cta_text: '',
                      }))
                    }
                    size="sm"
                    options={[
                      { value: 'image', label: 'Image' },
                      { value: 'video', label: 'Video' },
                      { value: 'html', label: 'HTML' },
                      { value: 'native', label: 'Custom Banner' },
                    ]}
                  />
                </div>
                )}
                <div className="flex items-center justify-between h-[34px]">
                  <label className="text-xs font-medium text-content-subtle">Lock WebView</label>
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, lock_webview: !prev.lock_webview }))}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${formData.lock_webview ? 'bg-primary' : 'bg-surface-muted'}`}
                  >
                    <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-surface shadow transition duration-200 ${formData.lock_webview ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </div>
              </div>

              {/* Click URL */}
              {formData.media_type !== 'html' && formData.ad_type !== 'popup' && (
                renderLinkUrlField('Click URL *', 'main')
              )}

              {/* Store URL (for cross-app referral) */}
              <div>
                <label htmlFor="store_url" className="block text-xs font-medium text-content-subtle mb-1">
                  Play Store URL
                  <span className="text-content-subtle font-normal ml-1">(override)</span>
                </label>
                <input
                  type="url"
                  id="store_url"
                  value={formData.store_url}
                  onChange={(e) => setFormData((prev) => ({ ...prev, store_url: e.target.value }))}
                  className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary"
                  placeholder="If empty, uses app default Store URL"
                />
              </div>

              {/* Duration (for video) */}
              {formData.media_type === 'video' && (
                <div className="w-32">
                  <label htmlFor="duration_sec" className="block text-xs font-medium text-content-subtle mb-1">Duration (sec)</label>
                  <input
                    type="number"
                    id="duration_sec"
                    value={formData.duration_sec ?? ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        duration_sec: e.target.value ? parseInt(e.target.value, 10) : null,
                      }))
                    }
                    min={1}
                    max={300}
                    className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="30"
                  />
                </div>
              )}

              {/* Native Ad Fields */}
              {formData.media_type === 'native' && (
                <div className="space-y-3 pt-2 border-t border-border">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-content-subtle">Custom Banner Settings</label>
                    <span className="text-xs text-content-subtle">{MEDIA_SPECS[formData.ad_type]?.native}</span>
                  </div>

                  {/* Icon upload */}
                  <div>
                    <label className="block text-xs font-medium text-content-subtle mb-1">Icon *</label>
                    <MediaUpload
                      value={formData.icon_url}
                      onChange={(url) => setFormData((prev) => ({ ...prev, icon_url: url }))}
                      onUpload={handleUpload}
                      accept="image/*"
                      mediaType="image"
                    />
                  </div>

                  {/* Headline */}
                  <div>
                    <label htmlFor="ad_text" className="block text-xs font-medium text-content-subtle mb-1">Headline *</label>
                    <input
                      type="text"
                      id="ad_text"
                      value={formData.ad_text}
                      onChange={(e) => setFormData((prev) => ({ ...prev, ad_text: e.target.value }))}
                      className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="Ad headline text"
                    />
                  </div>

                  {/* Body text */}
                  <div>
                    <label htmlFor="ad_body" className="block text-xs font-medium text-content-subtle mb-1">Body (optional)</label>
                    <textarea
                      id="ad_body"
                      value={formData.ad_body}
                      onChange={(e) => setFormData((prev) => ({ ...prev, ad_body: e.target.value }))}
                      rows={2}
                      className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="Ad description text"
                    />
                  </div>

                  {/* CTA text */}
                  <div>
                    <label htmlFor="cta_text" className="block text-xs font-medium text-content-subtle mb-1">CTA Button Text (optional)</label>
                    <input
                      type="text"
                      id="cta_text"
                      value={formData.cta_text}
                      onChange={(e) => setFormData((prev) => ({ ...prev, cta_text: e.target.value }))}
                      className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="Install, Learn More, Open..."
                    />
                  </div>

                  {/* Optional media image for medium/fullscreen native */}
                  {(formData.ad_type === 'interstitial' || formData.ad_type === 'rewarded') && (
                    <div>
                      <label className="block text-xs font-medium text-content-subtle mb-1">Media Image (optional, for fullscreen)</label>
                      <MediaUpload
                        value={formData.media_url}
                        onChange={(url) => setFormData((prev) => ({ ...prev, media_url: url }))}
                        onUpload={handleUpload}
                        accept="image/*"
                        mediaType="image"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Popup Ad Fields */}
              {formData.ad_type === 'popup' && (
                <div className="space-y-3 pt-2 border-t border-border">
                  <label className="text-xs font-medium text-content-subtle">Popup Settings</label>

                  {/* Title */}
                  <div>
                    <label htmlFor="popup_title" className="block text-xs font-medium text-content-subtle mb-1">Title *</label>
                    <input
                      type="text"
                      id="popup_title"
                      value={formData.ad_text}
                      onChange={(e) => setFormData((prev) => ({ ...prev, ad_text: e.target.value }))}
                      className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="Popup title"
                    />
                  </div>

                  {/* Subtitle */}
                  <div>
                    <label htmlFor="popup_subtitle" className="block text-xs font-medium text-content-subtle mb-1">Subtitle (optional)</label>
                    <input
                      type="text"
                      id="popup_subtitle"
                      value={formData.ad_body}
                      onChange={(e) => setFormData((prev) => ({ ...prev, ad_body: e.target.value }))}
                      className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="Popup subtitle text"
                    />
                  </div>

                  {/* Button 1 + Button 2 */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="popup_btn1" className="block text-xs font-medium text-content-subtle mb-1">Button 1 Text *</label>
                      <input
                        type="text"
                        id="popup_btn1"
                        value={formData.cta_text}
                        onChange={(e) => setFormData((prev) => ({ ...prev, cta_text: e.target.value }))}
                        className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary"
                        placeholder="Accept"
                      />
                    </div>
                    <div>
                      <label htmlFor="popup_btn2" className="block text-xs font-medium text-content-subtle mb-1">Button 2 Text (optional)</label>
                      <input
                        type="text"
                        id="popup_btn2"
                        value={formData.cta_text_2}
                        onChange={(e) => setFormData((prev) => ({ ...prev, cta_text_2: e.target.value }))}
                        className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary"
                        placeholder="Cancel"
                      />
                    </div>
                  </div>

                  {/* Link URL */}
                  {renderLinkUrlField('Link URL *', 'popup')}

                  {/* Delay + Repeat */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="popup_delay" className="block text-xs font-medium text-content-subtle mb-1">Show after (seconds)</label>
                      <input
                        type="number"
                        id="popup_delay"
                        value={formData.popup_delay_sec ?? ''}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            popup_delay_sec: e.target.value ? parseInt(e.target.value, 10) : null,
                          }))
                        }
                        min={0}
                        max={3600}
                        className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary"
                        placeholder="5"
                      />
                    </div>
                    <div>
                      <label htmlFor="popup_repeat" className="block text-xs font-medium text-content-subtle mb-1">Repeat every (seconds)</label>
                      <input
                        type="number"
                        id="popup_repeat"
                        value={formData.popup_repeat_sec ?? ''}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            popup_repeat_sec: e.target.value ? parseInt(e.target.value, 10) : null,
                          }))
                        }
                        min={0}
                        max={86400}
                        className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary"
                        placeholder="600"
                      />
                    </div>
                  </div>

                  {/* Optional image */}
                  <div>
                    <label className="block text-xs font-medium text-content-subtle mb-1">Image (optional)</label>
                    <MediaUpload
                      value={formData.media_url}
                      onChange={(url) => setFormData((prev) => ({ ...prev, media_url: url }))}
                      onUpload={handleUpload}
                      accept="image/*"
                      mediaType="image"
                    />
                  </div>
                </div>
              )}

              {/* Creative upload (non-native, non-popup) */}
              {formData.media_type !== 'native' && formData.ad_type !== 'popup' && (
                <div className="pt-2 border-t border-border">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-content-subtle">Creative *</label>
                    <span className="text-xs text-content-subtle">{MEDIA_SPECS[formData.ad_type]?.[formData.media_type]}</span>
                  </div>
                  <MediaUpload
                    value={formData.media_url}
                    onChange={(url) => setFormData((prev) => ({ ...prev, media_url: url }))}
                    onUpload={handleUpload}
                    accept={formData.media_type === 'image' ? 'image/*' : formData.media_type === 'html' ? '.html,.htm' : 'video/*'}
                    mediaType={formData.media_type}
                  />
                </div>
              )}
            </div>

            {/* Right: Targeting */}
            <div className="min-h-0 overflow-y-auto">
              <TargetingForm
                value={formData.targeting}
                onChange={(targeting) => setFormData((prev) => ({ ...prev, targeting }))}
                offerGeos={offerGeos}
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 mt-3 border-t border-border flex-shrink-0">
            {modalMode === 'edit' && selectedAd ? (
              <div className="flex gap-4 text-xs text-content-subtle">
                <span>Created: {formatDateTime(selectedAd.created_at)}</span>
                {selectedAd.updated_at && (
                  <span>Updated: {formatDateTime(selectedAd.updated_at)}</span>
                )}
              </div>
            ) : <div />}
            <div className="flex gap-3">
              <Button type="button" variant="secondary" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {modalMode === 'create' ? 'Create Ad' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={modalMode === 'delete'}
        onClose={closeModal}
        title="Delete Ad"
        size="sm"
      >
        <div className="space-y-4">
          {formError && (
            <div className="p-3 bg-danger-soft border border-danger/30 rounded-md">
              <p className="text-sm text-danger">{formError}</p>
            </div>
          )}

          <p className="text-sm text-content-muted">
            Are you sure you want to delete this ad? This action cannot be
            undone.
          </p>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleDelete}
              loading={deleteMutation.isPending}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* Duplicate to Campaign Modal */}
      <Modal
        isOpen={showDuplicateModal}
        onClose={() => { setShowDuplicateModal(false); setFormError(''); }}
        title="Duplicate Ads to Campaign"
        size="sm"
      >
        <div className="space-y-4">
          {formError && (
            <div className="p-3 bg-danger-soft border border-danger/30 rounded-md">
              <p className="text-sm text-danger">{formError}</p>
            </div>
          )}

          <p className="text-sm text-content-muted">
            Duplicate {selectedAdIds.size} ad{selectedAdIds.size > 1 ? 's' : ''} to another campaign.
            All settings and targeting will be copied.
          </p>

          <div>
            <label htmlFor="duplicate-target" className="block text-sm font-medium text-content-muted mb-1">
              Target Campaign
            </label>
            <Select
              value={duplicateTargetCampaign}
              onChange={(v) => setDuplicateTargetCampaign(v)}
              placeholder="Select campaign..."
              searchable
              options={[
                { value: '', label: 'Select campaign...' },
                ...activeCampaigns.map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => { setShowDuplicateModal(false); setFormError(''); }}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleDuplicate}
              disabled={!duplicateTargetCampaign}
              loading={duplicateMutation.isPending}
            >
              Duplicate
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
